import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import Button from "components/chalky-layout/button"
import Icon from "@kaspernj/api-maker/build/utils/icon"
import MoneyFormatter from "@kaspernj/api-maker/build/money-formatter"
import {PlanResourcePeriod} from "models"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import React from "react"
import Text from "@kaspernj/api-maker/build/utils/text"
import useCurrentCurrency from "components/utils/use-current-currency"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import {View} from "react-native"

export default React.memo(shapeComponent(class PeriodOption extends ShapeComponent {
  static propTypes = propTypesExact({
    monthlyPeriod: PropTypes.instanceOf(PlanResourcePeriod),
    onSelect: PropTypes.func.isRequired,
    period: PropTypes.instanceOf(PlanResourcePeriod).isRequired,
    selected: PropTypes.bool.isRequired
  })

  setup() {
    const {t} = useI18n({namespace: "js.components.plans.sign_up_modal.period_option"})

    this.setInstance({t})
    this.currentCurrency = useCurrentCurrency()
    this.price = this.priceForPeriod(this.p.period)

    if (!this.price) throw new Error(`No price for currency ${this.currentCurrency?.currencyCode()} could be found`)
  }

  render() {
    const {price, t} = this.tt
    const {monthlyPeriod, period} = this.p
    const priceLabel = MoneyFormatter.format(this.price?.price())
    let label

    if (period.hasName()) {
      label = period.name().replace("%{price}", priceLabel)
    } else {
      label = this.t(".x_months_for_price", {months: this.p.period.months(), price: priceLabel})
    }

    const pricePerMonth = price.priceCents() / period.months()
    const pricePerYear = pricePerMonth * 12
    const pricePerYearLabel = MoneyFormatter.format({amount: pricePerYear, currency: this.price.priceCurrency()})

    const discountPrice = React.useMemo(() => {
      if (!monthlyPeriod) return
      if (period.id() == monthlyPeriod.id()) return

      const monthlyPeriodPrice = this.priceForPeriod(monthlyPeriod)
      const monthlyPeriodPricePerYear = monthlyPeriodPrice.priceCents() * 12
      const discountPriceCents = monthlyPeriodPricePerYear - pricePerYear

      if (discountPriceCents > 0) {
        return {amount: discountPriceCents, currency: monthlyPeriodPrice.priceCurrency()}
      }
    }, [period.id(), pricePerYear, monthlyPeriod?.id()])

    return (
      <View
        style={this.rootViewStyle ||= {
          width: 403,
          padding: 48,
          border: "1px solid #e5dfff",
          borderRadius: 48
        }}
      >
        <Text style={this.labelTextStyle ||= {fontFamily: "HankenGrotesk", fontSize: 32, fontWeight: "bold"}}>
          {label}
        </Text>
        <Text style={this.priceLabelTextStyle ||= {marginTop: 24, fontFamily: "DreamChaser", fontSize: 64, fontWeight: 400}}>
          {priceLabel}
        </Text>
        <Text style={this.yearlyTotalTextStyle ||= {marginBottom: 24, fontFamily: "HankenGrotesk", fontSize: 14, fontWeight: "bold"}}>
          {t(".yearly_total", {yearly_total: pricePerYearLabel})}
        </Text>
        <Button
          dataSet={this.cache("chooseButtonDataSet", {
            component: "period-choose-button",
            periodId: period.id(),
            priceId: price?.id()
          }, [period.id(), price?.id()])}
          onPress={this.tt.onChoosePress}
          primary
          title={t(".choose")}
        />
        {discountPrice &&
          <View
            style={this.discountPriceViewStyle ||= {
              flexDirection: "row",
              alignItems: "center",
              position: "absolute",
              top: 0,
              right: 56,
              backgroundColor: "#e5dfff",
              borderBottomRightRadius: 8,
              borderBottomLeftRadius: 8,
              paddingHorizontal: 8,
              paddingVertical: 7
            }}
          >
            <Icon color="#2c2c96" name="piggy-bank" size={20} />
            <Text style={this.discountPriceTextStyle ||= {marginLeft: 10, color: "#2c2c96", fontSize: 14, fontWeight: "bold"}}>
              {t(".save_discount", {discount: MoneyFormatter.format(discountPrice)})}
            </Text>
          </View>
        }
      </View>
    )
  }

  onChoosePress = () => {
    this.p.onSelect({period: this.p.period, price: this.tt.price})
  }

  priceForPeriod(period) {
    if (!this.currentCurrency) throw new Error("No current currency")

    return period.prices().loaded().find((price) => price.priceCurrency() == this.currentCurrency.currencyCode())
  }
}))
